import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

interface LineItem {
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  is_included?: boolean;
}

interface EstimateLineItemSelectorProps {
  items: LineItem[];
  onItemsChange: (items: LineItem[], showAsLumpSum: boolean) => void;
}

export function EstimateLineItemSelector({ items, onItemsChange }: EstimateLineItemSelectorProps) {
  const [selectedItems, setSelectedItems] = useState<LineItem[]>(
    items.map(item => ({ ...item, is_included: item.is_included ?? true }))
  );
  const [showAsLumpSum, setShowAsLumpSum] = useState(false);

  useEffect(() => {
    onItemsChange(selectedItems, showAsLumpSum);
  }, [selectedItems, showAsLumpSum]);

  const handleToggleItem = (index: number) => {
    const updated = [...selectedItems];
    updated[index] = { ...updated[index], is_included: !updated[index].is_included };
    setSelectedItems(updated);
  };

  const handleSelectAll = () => {
    setSelectedItems(selectedItems.map(item => ({ ...item, is_included: true })));
  };

  const handleDeselectAll = () => {
    setSelectedItems(selectedItems.map(item => ({ ...item, is_included: false })));
  };

  const calculateTotal = () => {
    return selectedItems
      .filter(item => item.is_included)
      .reduce((sum, item) => sum + item.total, 0);
  };

  const includedCount = selectedItems.filter(item => item.is_included).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Select Line Items for Proposal</CardTitle>
        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button size="sm" variant="outline" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            {includedCount} of {selectedItems.length} selected
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-3">
          {selectedItems.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 rounded hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  checked={item.is_included}
                  onCheckedChange={() => handleToggleItem(index)}
                  id={`item-${index}`}
                />
                <Label
                  htmlFor={`item-${index}`}
                  className={`flex-1 cursor-pointer ${!item.is_included ? 'text-muted-foreground line-through' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{item.description}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span>{item.quantity} {item.unit}</span>
                      <span className="font-medium">${item.total.toFixed(2)}</span>
                    </div>
                  </div>
                </Label>
              </div>
            </div>
          ))}
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="lump-sum" className="font-medium">Show as Lump Sum</Label>
              <p className="text-sm text-muted-foreground">
                Hide individual line items and show single total price
              </p>
            </div>
            <Switch
              id="lump-sum"
              checked={showAsLumpSum}
              onCheckedChange={setShowAsLumpSum}
            />
          </div>

          <div className="flex justify-between items-center text-lg font-bold border-t pt-3">
            <span>Total:</span>
            <span>${calculateTotal().toFixed(2)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
